import type { TeamType } from './teams';

/**
 * Dashboard composition as DATA. The renderer reads this schema and assembles
 * each team's dashboard from widgets — so a new team type is a new config row,
 * not new code. (In production these rows live in a JSONB `dashboard_configs`
 * table, RLS-scoped per tenant; admins can reorder/customise.)
 */
export type WidgetType =
  | 'vitals'
  | 'queue'
  | 'category'
  | 'sla'
  | 'escalation'
  | 'changeCalendar'
  | 'severityMatrix'
  | 'mttr'
  | 'threats'
  | 'reviewPipeline'
  | 'sprintBoard'
  | 'intake'
  | 'approvals'
  | 'statusDistribution';

export type Widget = { type: WidgetType; title: string; span?: 1 | 2 | 3 };

export type DashboardConfig = { widgets: Widget[] };

export const DASHBOARDS: Record<TeamType, DashboardConfig> = {
  // IT / Service Desk — the classic ITSM cockpit
  it: {
    widgets: [
      { type: 'vitals', title: 'Team vitals', span: 3 },
      { type: 'statusDistribution', title: 'Ticket Status Distribution', span: 3 },
      { type: 'queue', title: 'Incident & request queue', span: 2 },
      { type: 'sla', title: 'SLA — first response & resolution', span: 1 },
      { type: 'category', title: 'Volume by category', span: 1 },
      { type: 'escalation', title: 'Escalation tiers (L1 → L3)', span: 1 },
      { type: 'changeCalendar', title: 'Change calendar', span: 1 },
    ],
  },
  // Security / SOC — threat & incident response
  soc: {
    widgets: [
      { type: 'vitals', title: 'SOC vitals', span: 3 },
      { type: 'queue', title: 'Incident queue', span: 3 },
      { type: 'severityMatrix', title: 'Incident severity matrix', span: 2 },
      { type: 'mttr', title: 'MTTD / MTTR', span: 1 },
      { type: 'category', title: 'By tactic', span: 1 },
      { type: 'threats', title: 'Active threats', span: 1 },
      { type: 'escalation', title: 'Escalation ladder', span: 1 },
    ],
  },
  // Development — sprint-aligned delivery
  dev: {
    widgets: [
      { type: 'vitals', title: 'Team vitals', span: 3 },
      { type: 'sprintBoard', title: 'Sprint board', span: 2 },
      { type: 'category', title: 'Bug vs feature', span: 1 },
      { type: 'queue', title: 'Incoming', span: 2 },
      { type: 'changeCalendar', title: 'Deploy calendar', span: 1 },
    ],
  },
  // Design — creative pipeline
  design: {
    widgets: [
      { type: 'vitals', title: 'Team vitals', span: 3 },
      { type: 'reviewPipeline', title: 'Creative review pipeline', span: 2 },
      { type: 'category', title: 'Requests by discipline', span: 1 },
      { type: 'queue', title: 'Request board', span: 3 },
    ],
  },
  // Generic — HR / Finance / Facilities / Legal
  generic: {
    widgets: [
      { type: 'vitals', title: 'Team vitals', span: 3 },
      { type: 'intake', title: 'Request intake', span: 1 },
      { type: 'approvals', title: 'Approval chains', span: 1 },
      { type: 'sla', title: 'SLA tracking', span: 1 },
      { type: 'queue', title: 'Open requests', span: 3 },
    ],
  },
};
